import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { setPath } from '@/lib/setSlug';

const SITE = 'https://lorescout.com';

interface SetRow {
  id: string;
  name: string;
  code: string;
  released_at: string | null;
  total_cards: number | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'TBA';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function SetsIndexPage() {
  const { data: sets, isLoading } = useQuery({
    queryKey: ['sets-index'],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sets')
        .select('*')
        .order('released_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as SetRow[];
    },
  });

  const today = new Date();
  const released = (sets || []).filter(s => s.released_at && new Date(s.released_at) <= today);
  const upcoming = (sets || []).filter(s => !s.released_at || new Date(s.released_at) > today);

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>Disney Lorcana Sets — Release Dates & Card Lists · LoreScout</title>
        <meta name="description" content="Every Disney Lorcana set — release dates, card counts, and full card lists with live prices. Track The First Chapter through Wilds Unknown on LoreScout." />
        <link rel="canonical" href={`${SITE}/sets`} />
        <meta property="og:title" content="Disney Lorcana Sets — Release Dates & Card Lists" />
        <meta property="og:description" content="Every Lorcana set with release dates, card counts, and full card lists." />
        <meta property="og:url" content={`${SITE}/sets`} />
        <meta name="twitter:title" content="Disney Lorcana Sets — Release Dates & Card Lists" />
        <meta name="twitter:description" content="Every Lorcana set with release dates, card counts, and full card lists." />
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}<span className="text-foreground">Sets</span>
        </nav>
        <h1 className="font-serif text-3xl">Disney Lorcana Sets</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every Lorcana set, released and upcoming. Tap any set for the full card list and live prices.
        </p>

        {isLoading && <div className="py-12 flex justify-center"><LoadingSpinner /></div>}

        {!!released.length && (
          <section className="mt-6">
            <h2 className="font-serif text-xl mb-3">Released</h2>
            <ul className="space-y-2">
              {released.map(s => (
                <li key={s.id}>
                  <Link to={setPath(s)} className="flex items-center justify-between rounded-md border border-border px-3 py-2 hover:bg-muted/40">
                    <span>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">Set {s.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(s.released_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!upcoming.length && (
          <section className="mt-8">
            <h2 className="font-serif text-xl mb-3">Upcoming</h2>
            <ul className="space-y-2">
              {upcoming.map(s => (
                <li key={s.id}>
                  <Link to={setPath(s)} className="flex items-center justify-between rounded-md border border-border px-3 py-2 hover:bg-muted/40">
                    <span>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">Set {s.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(s.released_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}
